import Download from "@mui/icons-material/Download";
import Preview from "@mui/icons-material/Preview";
import {
  Box,
  IconButton,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { xsHeight, mdHeight } from "components/AppBar";
import { FullScreenDocumentPreview } from "components/Document/shared/FullScreenDocumentPreview";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { tabsOffset } from "components/TabLinks";
import { gql } from "generated-graphql";
import { DocumentType, FacilityDocumentsSearchQuery } from "generated-graphql/graphql";
import { useDocumentDownload } from "hooks/useDocumentDownload";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { documentTypeToLabel } from "util/constants";
import { prettyPrintDateMed } from "util/dates";

export const FACILITY_DOCUMENTS_SECTION_ID = "facility-documents";
export const FACILITY_DOCUMENTS_SECTION_ICON = "📑";

const FACILITY_DOCUMENTS_SEARCH = gql(`
  query FacilityDocumentsSearch($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    documents(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        title
        fileExtension
        documentType
        createdAt
        storageLink
        sizeMegabytes
      }
      count
    }
  }
`);

type Row = FacilityDocumentsSearchQuery["documents"]["items"][number];

export const FacilityDocumentsSection: React.FC = () => {
  const theme = useTheme();
  const { tenantId, facilityId } = useParams<{ tenantId: string; facilityId: string }>();
  const { fetchAndHandleDownload } = useDocumentDownload();
  const [getDownloadLink] = useDocumentDownloadLink();
  const [previewState, setPreviewState] = useState<{
    open: boolean;
    fileUrl: string;
    fileExtension: string;
    title: string;
  }>({ open: false, fileUrl: "", fileExtension: "", title: "" });

  const handleView = useCallback(
    async (row: Row) => {
      const { data } = await getDownloadLink({ variables: { id: row.id } });
      if (data?.getDocumentDownloadLink) {
        setPreviewState({
          open: true,
          fileUrl: data.getDocumentDownloadLink,
          fileExtension: row.fileExtension ?? "",
          title: row.title,
        });
      }
    },
    [getDownloadLink]
  );

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      {
        field: "title",
        headerName: "Name",
        flex: 1.5,
      },
      {
        field: "fileExtension",
        headerName: "Format",
        flex: 0.5,
      },
      {
        field: "documentType",
        headerName: "Document Type",
        flex: 0.75,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(DocumentType),
        enumPresentationFunction: (enumValue: string) =>
          documentTypeToLabel(enumValue as DocumentType),
        valueGetter: (_, row) => (row.documentType ? documentTypeToLabel(row.documentType) : ""),
      },
      {
        field: "createdAt",
        headerName: "Uploaded",
        flex: 0.5,
        valueGetter: (_, row) => (row.createdAt ? prettyPrintDateMed(row.createdAt) : ""),
      },
      {
        field: "actions",
        headerName: "",
        flex: 0.4,
        sortable: false,
        filterable: false,
        align: "right",
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Tooltip title="Download">
              <IconButton
                size="small"
                sx={{ color: "action.active" }}
                onClick={async (e) => {
                  e.stopPropagation();
                  await fetchAndHandleDownload({ documentId: row.id });
                }}
              >
                <Download fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View">
              <IconButton
                size="small"
                sx={{ color: "action.active" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleView(row);
                }}
              >
                <Preview fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [fetchAndHandleDownload, handleView]
  );

  return (
    <Box
      id={FACILITY_DOCUMENTS_SECTION_ID}
      sx={{
        scrollMarginTop: {
          xs: theme.spacing(xsHeight + tabsOffset + 11),
          md: theme.spacing(mdHeight + tabsOffset + 11),
        },
      }}
    >
      <Typography variant="h6" mb={5}>
        {FACILITY_DOCUMENTS_SECTION_ICON} Facility Documents
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Showing all current facility documents. To see specific requirements for your program area,
        view{" "}
        <MuiLink component={Link} to={`/o/${tenantId}/chemicals/documents/${facilityId}`}>
          Chemicals Documents
        </MuiLink>
        .
      </Typography>

      <OmnisearchDataGrid
        persistenceKey={`facility-documents-v2-${facilityId}`}
        columns={columns}
        dataQuery={FACILITY_DOCUMENTS_SEARCH}
        defaultSearch={`tenantId:${tenantId} facilityId:${facilityId} excludeRejectedSubmissionDocuments:true`}
        getItems={(data) => data.documents.items}
        getCount={(data) => data.documents.count ?? 0}
        initialSortModel={[{ field: "createdAt", sort: "desc" }]}
        noDataMessage="No documents for this facility."
        excludeFilterColumns={["createdAt", "fileExtension", "documentType"]}
        additionalFilterColumns={[
          {
            key: "documentType",
            header: "Document Type",
            filterKeyType: "enumAutocomplete",
            enumValues: Object.values(DocumentType),
            enumPresentationFunction: (enumValue) => documentTypeToLabel(enumValue as DocumentType),
          },
          {
            header: "Tag",
            key: "tags",
          },
        ]}
        rowHeight="auto"
        disableRowSelectionOnClick
        showManageColumns={false}
        gridSx={{
          "& .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within":
            { outline: "none" },
          "& .MuiDataGrid-columnSeparator": { display: "none" },
          "& .MuiDataGrid-row:hover": {
            cursor: "default",
            backgroundColor: "transparent",
          },
          "& .MuiDataGrid-row.Mui-hovered": {
            backgroundColor: "transparent",
          },
        }}
      />

      <FullScreenDocumentPreview
        open={previewState.open}
        onClose={() => setPreviewState((prev) => ({ ...prev, open: false }))}
        fileUrl={previewState.fileUrl}
        fileExtension={previewState.fileExtension}
        title={previewState.title}
      />
    </Box>
  );
};
